import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { waitForPendingWrites } from "firebase/firestore";
import { db } from "../../services/firebase";
import { startAppVersionMonitor } from "../../services/appVersionMonitor";
import { flushPendingEditors } from "../../utils/appUpdateSafety";

const BUILD_ID = process.env.REACT_APP_BUILD_ID;

export default function AppVersionUpdate() {
  const location = useLocation();
  const monitor = useRef(null);
  const editedView = useRef(false);
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    // Unknown forms remain protected until the user leaves their view. Registered
    // autosave fields can be flushed safely without blocking later updates.
    const protectEdit = (event) => {
      if (event.type === "pointerdown" && !event.target.closest?.('button, [role="button"], [draggable="true"], input[type="file"]')) return;
      if (!event.target.closest?.("[data-autosave-editor]")) editedView.current = true;
    };
    document.addEventListener("input", protectEdit, true);
    document.addEventListener("change", protectEdit, true);
    document.addEventListener("dragstart", protectEdit, true);
    document.addEventListener("pointerdown", protectEdit, true);
    monitor.current = startAppVersionMonitor({
      buildId: BUILD_ID,
      onUpdateAvailable: setAvailable,
      canReload: () => !editedView.current &&
        !document.activeElement?.matches("input, textarea, select, [contenteditable=true]") &&
        !document.querySelector('[role="dialog"], [aria-modal="true"]'),
      prepareReload: async () => {
        if (!await flushPendingEditors()) return false;
        let timeout;
        try {
          await Promise.race([
            waitForPendingWrites(db),
            new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error("Pending saves")), 10000); }),
          ]);
          return true;
        } finally { clearTimeout(timeout); }
      },
    });
    return () => {
      monitor.current.stop();
      document.removeEventListener("input", protectEdit, true);
      document.removeEventListener("change", protectEdit, true);
      document.removeEventListener("dragstart", protectEdit, true);
      document.removeEventListener("pointerdown", protectEdit, true);
    };
  }, []);

  useEffect(() => {
    editedView.current = false;
    monitor.current?.check();
  }, [location.pathname]);

  return available ? <div role="status" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[600] rounded-xl border border-[color:var(--border)] bg-[var(--surface-1)] px-4 py-2 text-xs text-[var(--text-muted)] shadow-lg">
    Update ready · loads automatically after saving or leaving this view.
  </div> : null;
}
